// src/services/notificationService.js
// 80차 계속: 53차에 fcmToken 미생성 원인 추적용으로 심어뒀던 DEBUG_PUSH/dbg() 단계별 Alert
// 계측(원래 주석에 "원인 확인 후 반드시 제거"라고 명시돼 있었으나 안 지켜지고 있었음, 스위치
// 자체는 이미 꺼져있어 기능상 무해했지만 죽은 코드라 정리 — 하드코딩/데드코드 재검토로 발견).

import * as Notifications from 'expo-notifications';
import * as Device        from 'expo-device';
import { Platform } from 'react-native';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db }             from './firebase';

// app.json extra.eas.projectId
const EAS_PROJECT_ID = '794131e0-970e-4ea8-aea9-8d31091340a9';

// 포그라운드 알림 표시 설정 (앱이 켜져 있을 때도 배너 표시)
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge:  true,
  }),
});

// ─── FCM 토큰 등록 & Firestore 저장 ─────────────────────────────────────────
export const registerPushToken = async (uid) => {
  if (!uid || uid === 'guest') return;

  // 실기기 체크 — 시뮬레이터면 토큰 발급 불가
  if (!Device.isDevice) return;

  // 알림 권한 확인/요청
  let finalStatus;
  try {
    const { status: existing } = await Notifications.getPermissionsAsync();
    finalStatus = existing;
    if (existing !== 'granted') {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }
  } catch (e) {
    return;
  }
  if (finalStatus !== 'granted') return;

  // Android 채널 설정 (iOS는 스킵)
  if (Platform.OS === 'android') {
    try {
      await Notifications.setNotificationChannelAsync('reservations', {
        name: '예약 알림',
        importance: Notifications.AndroidImportance.HIGH,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#d4a843',
        sound: 'default',
      });
    } catch (e) { /* 채널 설정 실패해도 토큰 발급은 계속 진행 */ }
  }

  // Expo Push 토큰 발급
  let token;
  try {
    const tokenData = await Notifications.getExpoPushTokenAsync({
      projectId: EAS_PROJECT_ID,
    });
    token = tokenData.data;
  } catch (e) {
    // 여기서 막히면 보통: EAS Push 인증서 미등록 / projectId 불일치 / capability 문제
    return;
  }

  // Firestore 저장
  // 78차 55차: 기존엔 fcmToken 단일 필드라 같은 계정을 다른 기기에서 로그인하면 이전
  // 기기의 토큰이 덮어써져 그 기기가 조용히 푸시를 못 받게 됐음 — fcmTokens 배열에
  // arrayUnion으로 추가(여러 기기 전부 유지). 만료된 토큰은 서버(onNotificationCreated)가
  // DeviceNotRegistered 응답을 받으면 그 토큰만 배열에서 제거.
  try {
    await updateDoc(doc(db, 'users', uid), { fcmTokens: arrayUnion(token) });
  } catch (e) {
    // 여기서 막히면 보통: Firestore Rules / 네트워크 / users 문서 없음
    return;
  }

  return token;
};

// 79차: [버그 수정] 로그아웃 시 이 기기의 푸시 토큰을 users.fcmTokens에서 지우지 않고
// 있었음 — 78차55차가 "여러 기기가 같은 계정에 로그인해도 서로 토큰을 안 덮어쓰게"
// fcmTokens를 배열로 바꿨을 때, 반대 방향(로그아웃/기기 공유 시 이전 계정의 토큰을
// 정리)은 빠져있었던 것. 공용 기기(매장 태블릿, 지인에게 잠깐 빌려준 폰 등)에서
// A가 로그아웃하고 B가 로그인해도 그 기기 토큰이 A의 fcmTokens에 계속 남아, A에게 오는
// 예약/크루/모집 알림(내용에 개인정보 포함)이 로그아웃 이후에도 그 기기에 계속
// 도착할 수 있었음 — Expo push token은 "기기+앱 설치" 단위라 로그인 여부와 무관하게
// 동일하므로, uninstall 전까지는 DeviceNotRegistered로도 자연 정리되지 않음.
// authService.js의 logout()이 signOut() 직전에 호출.
export const unregisterPushToken = async (uid) => {
  if (!uid || uid === 'guest' || !Device.isDevice) return;
  try {
    const { status } = await Notifications.getPermissionsAsync();
    if (status !== 'granted') return; // 애초에 토큰 발급 자체가 안 됐을 것
    const { data: token } = await Notifications.getExpoPushTokenAsync({ projectId: EAS_PROJECT_ID });
    await updateDoc(doc(db, 'users', uid), { fcmTokens: arrayRemove(token) });
  } catch {
    // 실패해도 로그아웃 자체를 막지 않음 — 무음 처리
  }
};

// ─── 알림 수신 리스너 설정 ───────────────────────────────────────────────────
export const setupNotificationListeners = ({ onNotification, onNotificationResponse } = {}) => {
  const foregroundSub = Notifications.addNotificationReceivedListener(notification => {
    const { title, body, data } = notification.request.content;
    onNotification?.({ title, body, data });
  });

  const responseSub = Notifications.addNotificationResponseReceivedListener(response => {
    const data = response.notification.request.content.data;
    onNotificationResponse?.({ data });
  });

  // 79차: [버그 수정] 앱이 완전히 종료된(killed) 상태에서 푸시를 탭해 새로 시작되는
  // 경우, JS가 기동해 위 addNotificationResponseReceivedListener를 등록하기 전에
  // 이미 탭 이벤트가 발생해 놓칠 수 있음(전수 정독으로 발견 — expo-notifications
  // 공식 권장 패턴은 콜드스타트를 getLastNotificationResponseAsync()로 별도 확인하는
  // 것인데 이 호출이 코드베이스 전체에 아예 없었음). 지금까지 이 세션에서 고친
  // "콜드스타트 딥링크 case 누락"들은 앱이 백그라운드에 살아있던(웜) 상태에서는
  // 여전히 유효하지만, 완전 종료 후 탭이라는 가장 흔한 실사용 시나리오 하나가
  // 이 호출 없이는 통째로 무반응이었을 것.
  Notifications.getLastNotificationResponseAsync().then(response => {
    if (response) {
      const data = response.notification.request.content.data;
      onNotificationResponse?.({ data });
    }
  }).catch(() => {});

  return () => {
    foregroundSub.remove();
    responseSub.remove();
  };
};